import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  TextField,
  IconButton,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MicIcon from "@mui/icons-material/Mic";
import classes from "./SegmenterChat.module.scss";
import { useSelector, useDispatch } from "react-redux";
import segmenterSectionAPIService from "../../services/api_service";
import { setChatHistories, setChatScrollPositions } from "../../store/slices/segmenterChatSlice";
import { getFormattedTime } from "../../utils/date";
import ReactMarkdown from 'react-markdown';
import CloseIcon from '@mui/icons-material/Close';
import { useSpeechRecognition } from '../../utils/speechRecognition';
import {
  handleSpeechInputChange,
  handleSpeechKeyDown,
  startListening as startListeningHelper,
  stopListening as stopListeningHelper
} from '../../utils/speechRecognitionHelpers';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchSummarizedChatDownloadFile } from "store";
import { downloadMarkdownAsPDF } from "utils/downloadMarkdownAsPDF";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";


const Chat = React.memo(({ clusterOutputString }) => {
  const [input, setInput] = useState("");
  // Speech-to-text states
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const finalRef = useRef(""); // stable storage for current final transcript across callbacks

  const recognitionRef = useRef(null);
  // Speech Recognition setup
  useSpeechRecognition({
    finalRef,
    setInput,
    setListening,
    setSupported,
    recognitionRef,
    lang: 'en-US',
    continuous: true,
    interimResults: true,
  });

  const startListening = () => startListeningHelper(recognitionRef, setListening, input, finalRef);
  const stopListening = () => stopListeningHelper(recognitionRef, setListening);

  const dispatch = useDispatch();
  const reduxChatHistories = useSelector((state) => state.segmenterChat.chatHistories);
  const chatHistories = useMemo(() => {
    const initialChat = [
      {
        type: "api",
        message:
          "Hi, I can help you with answering questions about each audience segment. Please enter your query, for example: “What are the key demographics for Segment X?",
        timestamp: getFormattedTime(),
      },
    ];
    return reduxChatHistories && reduxChatHistories.length > 0 ? reduxChatHistories : [initialChat];
  }, [reduxChatHistories]);
  const [selectedChat, setSelectedChat] = useState(0);
  const chatBoxRef = useRef(null);
  const chatScrollPositions = useSelector((state) => state.segmenterChat.chatScrollPositions || {});

  const [isLoading, setIsLoading] = useState(false);
  const { importFileName } = useSelector((state) => state.segmenter);
  const { isChatDownloading } = useSelector((state) => state.segmenterChat);

  const fetchChatResponse = async (conversation) => {
    try {
      const payload = {
        conversation: conversation,
        cluster_output_string: clusterOutputString,
        input_file: importFileName,
      }
      const data = await segmenterSectionAPIService.fetchChatResponse(payload);
      return data.conversation[data.conversation.length - 1].message;
    } catch (error) {
      console.error(error);
      return "Error fetching response";
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    stopListening()

    const userMessage = {
      type: "user",
      message: input,
      timestamp: getFormattedTime(),
    };

    const updatedChats = [...chatHistories];
    updatedChats[selectedChat] = [...updatedChats[selectedChat], userMessage];
    dispatch(setChatHistories(updatedChats));

    setIsLoading(true);

    const conversationHistory = [
      ...updatedChats[selectedChat].map((chat) => ({
        type: chat.type === "user" ? "user" : "api",
        message: chat.message,
      })),
      { type: "user", message: input },
    ];

    const apiResponse = await fetchChatResponse(conversationHistory);

    const updatedChatsWithApi = [...updatedChats];
    updatedChatsWithApi[selectedChat] = [
      ...updatedChatsWithApi[selectedChat],
      { type: "api", message: apiResponse, timestamp: getFormattedTime() },
    ];
    dispatch(setChatHistories(updatedChatsWithApi));

    setIsLoading(false);
    setInput("");
    finalRef.current = '';
  };

  const handleKeyDown = (event) => handleSpeechKeyDown(event, stopListening, handleSendMessage);

  // Update scroll position in Redux on every scroll
  useEffect(() => {
    const chatBox = chatBoxRef.current;
    if (!chatBox) return;
    const handleScroll = () => {
      dispatch(setChatScrollPositions({
        ...chatScrollPositions,
        [selectedChat]: chatBox.scrollTop,
      }));
    };
    chatBox.addEventListener('scroll', handleScroll);
    return () => {
      chatBox.removeEventListener('scroll', handleScroll);
    };
  }, [chatBoxRef, selectedChat, chatScrollPositions, dispatch]);

  const handleTabChange = useCallback((event, newValue) => {
    setSelectedChat(newValue);
  }, []);



  // Restore scroll position when selectedChat changes
  useEffect(() => {
    if (chatBoxRef.current && chatScrollPositions[selectedChat] !== undefined) {
      chatBoxRef.current.scrollTop = chatScrollPositions[selectedChat];
    }
  }, [selectedChat, chatScrollPositions]);

  const handleAddChat = useCallback(() => {
    const updatedChats = [...chatHistories, []];
    dispatch(setChatHistories(updatedChats));
    setSelectedChat(updatedChats.length - 1);
  }, [dispatch, chatHistories]);

  const handleCloseChat = useCallback((event, indexToRemove) => {
    event.stopPropagation();
    event.preventDefault();

    if (!chatHistories) return;

    const updatedChats = chatHistories.filter((_, i) => i !== indexToRemove);
    dispatch(setChatHistories(updatedChats));

    if (selectedChat === indexToRemove) {
      const newSelected = Math.max(0, indexToRemove - 1);
      setSelectedChat(updatedChats.length > 0 ? newSelected : -1);
    } else if (selectedChat > indexToRemove) {
      setSelectedChat(selectedChat - 1);
    }
  }, [chatHistories, dispatch, selectedChat]);

  const tabs = useMemo(
    () => (
      <Tabs
        value={selectedChat}
        onChange={handleTabChange}
        aria-label="chat tabs"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        className={classes.tabs}
      >
        {chatHistories.map((chat, index) => (
          <Tab key={index} disabled={isLoading} label={
            <div className={classes.tabLabel}>
              <span className={classes.tabSpan}>{`Chat ${index + 1}`}</span>
              {chatHistories.length > 1 && (
                <IconButton
                  size="small"
                  aria-label={`close chat ${index + 1}`}
                  onClick={(e) => handleCloseChat(e, index)}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          } />
        ))}
        <IconButton
          className={classes.addChatButton}
          aria-label="add chat"
          onClick={handleAddChat}
          disabled={isLoading}
        >
          <AddIcon />
        </IconButton>
      </Tabs>
    ),
    [selectedChat, chatHistories, handleAddChat, handleTabChange, handleCloseChat, isLoading]
  );

  // Separate function for handling input change
  const handleInputChange = handleSpeechInputChange(setInput, finalRef);

  const handleChatDownload = async () => {
    try {
      dispatch(fetchSummarizedChatDownloadFile({ seg_conversation: chatHistories[selectedChat], filename: "Audience_match_chat_summary.pdf" })).unwrap()
        .then((blob) => {
          const text = blob.text;
          downloadMarkdownAsPDF(text, 'Audience_match_chat_summary.pdf');
          showSnackbar("Download successful! You have downloaded the summarized PDF of chat");
        })
        .catch((err) => {
        });
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className={classes.chatContainer}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }} className={classes.chatHeader}>
        {tabs}
        <div className={classes.chatStack}>
          {chatHistories[selectedChat].length > 1 ?  <Tooltip title="Download as PDF">
            <button onClick={handleChatDownload} variant="contained" className={classes.chatDownload} disabled={isLoading}>
              {isChatDownloading ? <CircularProgress size={20} color="primary" /> : <DownloadIcon fontSize="medium" />}
            </button>
          </Tooltip> : null}
        </div>
      </Box>

      <div className={classes.chatBox} ref={chatBoxRef}>
        {chatHistories[selectedChat]?.map((chat, index) => (
          <div
            key={index}
            className={
              chat.type === "user" ? classes.userMessage : classes.apiMessage
            }
          >
            <div className={classes.chatBubble}>
              <ReactMarkdown>{chat?.message}</ReactMarkdown>
            </div>
            <div className={classes.timestamp}>{chat.timestamp}</div>
          </div>
        ))}

        {isLoading && (
          <div className={classes.typingIndicator}>
            <CircularProgress size={20} />
          </div>
        )}
      </div>

      <div className={classes.inputContainer}>
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={3}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={classes.textInput}
          autoComplete="off"
          placeholder="Type your message..."
          disabled={isLoading}
          inputRef={el => {
            if (el && listening === true) {
              el.selectionStart = el.value.length;
              el.selectionEnd = el.value.length;
              el.focus();
              // Move caret to the end (for textarea)
              const len = el.value.length;
              el.setSelectionRange(len, len);
              el.scrollLeft = el.scrollWidth;
            }
          }}
        />
        {/* Speech to Text Buttons */}
        {supported ? (
          <>
            <IconButton
              onClick={listening ? stopListening : startListening}
              color={listening ? "secondary" : "primary"}
              aria-label="speech to text"
              className={`${classes.sendButton} ${listening ? classes.micListening : ""}`}
              disabled={isLoading}
            >
              <MicIcon
                style={
                  listening
                    ? {
                      animation: "pulse 1s infinite",
                      color: "#d32f2f",
                      filter: "drop-shadow(0 0 6px #d32f2f)",
                    }
                    : {}
                }
              />
            </IconButton>
          </>
        ) : (
          <span style={{ color: "red", marginRight: 8, fontSize: 12 }}>
            Speech API not supported
          </span>
        )}
        <IconButton
          onClick={handleSendMessage}
          color="primary"
          aria-label="send message"
          className={classes.sendButton}
          disabled={isLoading}
        >
          <ArrowUpwardIcon />
        </IconButton>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
      {/* Add keyframes for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
});

export default Chat;
